import {Preview} from 'platform/components';
import {
  Box,
  HStack,
  Integer,
  makeStorageUrl,
  Show,
  StorageImage,
  ThemeIconKey,
  type BoxProps,
  useDevice,
} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {round} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {IconWithActions} from '../../EntityHeader/components/IconWithActions';
import type {EntityHeaderImageVariant} from '../types';
import {EntityHeaderNavigation} from './EntityHeaderNavigation';
import {EntityHeaderTitle} from './EntityHeaderTitle';

export interface EntityHeaderImageV2Props extends TestIdProps {
  image?: string;
  onImageClick?: () => void;
  icon?: ThemeIconKey;
  ratio?: `${number}/${number}`;
  title?: string;
  isTitleLoading?: boolean;
  imageVariant?: EntityHeaderImageVariant;
}

export function EntityHeaderImageV2(props: EntityHeaderImageV2Props) {
  const {imageVariant = 'compact'} = props;

  const device = useDevice();
  const isMobileLayout = device === 'mobile' || device === 'tablet';

  const RATIO = props.ratio ? parseRatio(props.ratio) : 4 / 3;
  const HEIGHT_IN_STEPS: Integer = 30;
  const WIDTH_IN_STEPS = (HEIGHT_IN_STEPS * RATIO) as Integer;
  const ICON_SIZE = round(HEIGHT_IN_STEPS / 2) as Integer;

  if (!props.icon && !props.image) {
    return null;
  }

  const containerProps = match(isMobileLayout)
    .returnType<BoxProps>()
    .with(true, () => ({
      width: '100%',
      height: 'auto',
    }))
    .otherwise(() => ({
      width: WIDTH_IN_STEPS,
      height: HEIGHT_IN_STEPS,
    }));

  const imageProps = match(isMobileLayout)
    .with(true, () => ({
      ratio: RATIO,
    }))
    .otherwise(() => ({
      width: WIDTH_IN_STEPS,
      height: HEIGHT_IN_STEPS,
    }));
  const isMobileImageVariant = isMobileLayout && imageVariant === 'image';
  const hasOnlyIcon = props.icon && !props.image;

  return (
    <Box {...containerProps}>
      <Box
        overflow="hidden"
        backgroundColor="palettes.neutral.30.100"
        position="relative"
        width="100%"
        borderRadius={isMobileImageVariant ? undefined : 'medium'}
        ratio={isMobileImageVariant ? String(RATIO) : undefined}
      >
        <Show onMobile onTablet>
          <EntityHeaderNavigation />
        </Show>

        <Show when={props.image}>
          <Show when={props.onImageClick}>
            <Preview
              url={props.image ?? ''}
              makeUrl={makeStorageUrl}
              fit="cover"
              onClick={props.onImageClick!}
              data-testid={suffixTestId('entityHeaderImage-image', props)}
              {...imageProps}
            />
          </Show>
          <Show when={!props.onImageClick}>
            <StorageImage
              url={props.image}
              fit="cover"
              data-testid={suffixTestId('entityHeaderImage-image', props)}
              {...imageProps}
            />
          </Show>
          <Show onMobile onTablet>
            <GradientOverlay
              data-testid={suffixTestId('entityHeaderImage-gradientOverlay', props)}
            />
          </Show>
        </Show>

        <Show onMobile onTablet when={hasOnlyIcon}>
          {match(imageVariant)
            .with('compact', () => (
              <Box padding={3} paddingTop={14}>
                <HStack align="center" spacing={3}>
                  <IconWithActions
                    width="auto"
                    height="auto"
                    icon={props.icon}
                    iconSize={12}
                    onClick={props.onImageClick}
                    data-testid={suffixTestId('entityHeaderImage-icon', props)}
                  />
                  <EntityHeaderTitle
                    title={props.title}
                    isLoading={props.isTitleLoading}
                    size={4}
                    data-testid={suffixTestId('entityHeaderImage-compactTitle', props)}
                  />
                </HStack>
              </Box>
            ))
            .with('image', () => (
              <HStack width="100%" height="100%" align="center" justify="center">
                <IconWithActions
                  width={WIDTH_IN_STEPS}
                  height={HEIGHT_IN_STEPS}
                  icon={props.icon}
                  iconSize={ICON_SIZE}
                  onClick={props.onImageClick}
                  data-testid={suffixTestId('entityHeaderImage-icon', props)}
                />
              </HStack>
            ))
            .exhaustive()}
        </Show>

        <Show onDesktop onNotebook when={hasOnlyIcon}>
          <HStack width="100%" height="100%" align="center" justify="center">
            <IconWithActions
              width={WIDTH_IN_STEPS}
              height={HEIGHT_IN_STEPS}
              icon={props.icon}
              iconSize={ICON_SIZE}
              onClick={props.onImageClick}
              data-testid={suffixTestId('entityHeaderImage-icon', props)}
            />
          </HStack>
        </Show>

        <Show when={props.title && imageVariant === 'image'}>
          <Box position="absolute" bottom={0} left={0} width="100%" padding={3}>
            <EntityHeaderTitle
              data-testid={suffixTestId('entityHeaderImage-title', props)}
              isLoading={props.isTitleLoading}
              title={props.title}
              color={hasOnlyIcon ? 'primary' : ['white', 'white', 'primary']}
            />
          </Box>
        </Show>
      </Box>
    </Box>
  );
}

const parseRatio = (ratio: `${number}/${number}`) => {
  const [w, h] = ratio.split('/').map(Number);
  const isValid = Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0;

  if (!isValid) {
    return 4 / 3;
  }

  return w / h;
};

const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 9%,
    rgba(0, 0, 0, 0.5) 70%,
    rgba(0, 0, 0, 1) 100%
  );
  pointer-events: none;
`;
